import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import PriceRange from '../components/PriceRange';
import Skeleton from '../components/Skeleton';
import { STORAGE_BASE } from '../config';
import Modal from '../components/Modal';
import { Search as SearchIcon, X as XIcon, SlidersHorizontal, ChevronDown, Check } from 'lucide-react';
import { applySeo } from './HomePage';

const SORT_OPTIONS = [
  { value: 'newest', label: 'جدیدترین' },
  { value: 'price_asc', label: 'قیمت: کم به زیاد' },
  { value: 'price_desc', label: 'قیمت: زیاد به کم' },
  { value: 'name', label: 'نام' },
];

function SortSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const current = SORT_OPTIONS.find(o => o.value === value) || SORT_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const onDown = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div className="ap-select" ref={rootRef}>
      <button type="button" className="ap-select-trigger" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open}>
        <span>{current.label}</span>
        <ChevronDown size={16} strokeWidth={1.8} className={`ap-select-chevron${open ? ' is-open' : ''}`} />
      </button>
      {open && (
        <ul className="ap-select-list" role="listbox">
          {SORT_OPTIONS.map(o => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={`ap-select-option${o.value === value ? ' is-selected' : ''}`}
                onClick={() => { onChange(o.value); setOpen(false); }}
              >
                {o.value === value && <Check size={14} strokeWidth={2.2} />}
                <span>{o.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function CategoryPage() {
  const { slug } = useParams();

  // --- filter state -------------------------------------------------
  const [category, setCategory] = useState(null);
  const [sort, setSort] = useState('newest');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [attrs, setAttrs] = useState({});
  const [priceRange, setPriceRange] = useState({ lo: '', hi: '' });
  const [priceBounds, setPriceBounds] = useState({ min: 0, max: 500000 });
  const [mobileFilter, setMobileFilter] = useState(false);

  // --- data state -----------------------------------------------------
  const [facets, setFacets] = useState({ attributes: [], price: {} });
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const filterKeyRef = useRef('');

  // debounce free-text search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // reset local filters and re-resolve the category whenever the slug changes
  useEffect(() => {
    setAttrs({});
    setSearchInput('');
    setSearch('');
    (async () => {
      try {
        const cats = await axios.get('/categories');
        const c = (cats.data || []).find(x => x.slug === slug);
        setCategory(c || { id: null, name: 'همه محصولات' });
        if (c) applySeo(c, c.name);
        const f = await axios.get('/filter-facets');
        setFacets(f.data);
        if (f.data.price) {
          setPriceBounds({ min: f.data.price.min, max: f.data.price.max });
          setPriceRange({ lo: f.data.price.min, hi: f.data.price.max });
        }
      } catch {}
    })();
  }, [slug]);

  const fetchProducts = useCallback(async (targetPage, replace) => {
    if (replace) setLoading(true); else setLoadingMore(true);
    const params = { sort, search, page: targetPage, limit: 12 };
    if (category?.id) params.category_id = category.id;
    if (priceRange.lo !== '') params.min_price = priceRange.lo;
    if (priceRange.hi !== '') params.max_price = priceRange.hi;
    Object.entries(attrs).forEach(([k, v]) => { if (v) params[`attributes[${k}]`] = v; });
    try {
      const r = await axios.get('/products-filter', { params });
      setProducts(prev => (replace ? (r.data.data || []) : [...prev, ...(r.data.data || [])]));
      setTotal(r.data.total || 0);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [sort, search, priceRange, attrs, category?.id]);

  // reset to page 1 whenever a filter actually changes; fetch page 1 or append
  useEffect(() => {
    if (category === null) return; // wait for the category to resolve first
    const key = JSON.stringify({ sort, search, priceRange, attrs, categoryId: category?.id });
    const isNewFilter = key !== filterKeyRef.current;
    filterKeyRef.current = key;
    if (isNewFilter) {
      setPage(1);
      fetchProducts(1, true);
    } else {
      fetchProducts(page, page === 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, search, priceRange, attrs, category, page]);

  const toggleAttr = (name, value) => setAttrs(prev => ({ ...prev, [name]: prev[name] === value ? '' : value }));
  const clearFilters = () => {
    setAttrs({});
    setPriceRange({ lo: priceBounds.min, hi: priceBounds.max });
    setSearchInput('');
    setSearch('');
  };
  const imgOf = (p) => (p.media?.[0]?.path ? STORAGE_BASE + '/' + p.media[0].path : p.image_path ? STORAGE_BASE + '/' + p.image_path : 'https://via.placeholder.com/500x500?text=Silver');

  const activeAttrEntries = Object.entries(attrs).filter(([, v]) => v);
  const priceIsFiltered = priceRange.lo !== priceBounds.min || priceRange.hi !== priceBounds.max;
  const hasActiveFilters = activeAttrEntries.length > 0 || priceIsFiltered || search;

  const searchBar = (
    <div className="ap-searchbar">
      <div className="ap-search-input">
        <SearchIcon size={18} strokeWidth={1.8} />
        <input
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="جستجو در محصولات..."
          aria-label="جستجو در محصولات"
        />
        {searchInput && (
          <button type="button" className="ap-search-clear" onClick={() => setSearchInput('')} aria-label="پاک کردن جستجو">
            <XIcon size={15} />
          </button>
        )}
      </div>
      <button type="button" className="ap-filter-toggle" onClick={() => setMobileFilter(true)} aria-label="نمایش فیلترها">
        <SlidersHorizontal size={18} strokeWidth={1.8} />
        {hasActiveFilters && <span className="ap-filter-dot" />}
      </button>
    </div>
  );

  const filterPanel = (
    <div className="ap-filters">
      <div className="ap-filters-head">
        <span>فیلترها</span>
        {hasActiveFilters && (
          <button type="button" className="ap-clear-btn" onClick={clearFilters}>پاک کردن همه</button>
        )}
      </div>

      <div className="ap-filter-group">
        <label>محدوده قیمت (تومان)</label>
        <PriceRange min={priceBounds.min} max={priceBounds.max} value={priceRange} onChange={setPriceRange} />
      </div>

      {facets.attributes && facets.attributes.map(a => (
        <div key={a.name} className="ap-filter-group">
          <label>{a.name}</label>
          <div className="ap-chips">
            {a.values.map(v => {
              const active = attrs[a.name] === v;
              return (
                <button
                  key={v}
                  type="button"
                  className={`ap-chip${active ? ' is-active' : ''}`}
                  onClick={() => toggleAttr(a.name, v)}
                  aria-pressed={active}
                >
                  {v}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="ap-page w-full py-8" dir="rtl">
      <style>{apStyles}</style>

      <header className="ap-header">
        <h1 className="serif ap-title">{category ? category.name : 'دسته‌بندی'}</h1>
        <p className="ap-subtitle">فیلتر بر اساس ویژگی‌ها و محدوده قیمت</p>
      </header>

      <div className="ap-layout">
        <aside className="ap-sidebar">
          {filterPanel}
        </aside>

        <div className="ap-main">
          {searchBar}

          {hasActiveFilters && (
            <div className="ap-active-filters">
              {search && (
                <span className="ap-tag">
                  «{search}»
                  <button type="button" onClick={() => setSearchInput('')} aria-label="حذف فیلتر جستجو"><XIcon size={12} /></button>
                </span>
              )}
              {activeAttrEntries.map(([k, v]) => (
                <span className="ap-tag" key={k}>
                  {k}: {v}
                  <button type="button" onClick={() => toggleAttr(k, v)} aria-label={`حذف فیلتر ${k}`}><XIcon size={12} /></button>
                </span>
              ))}
              {priceIsFiltered && (
                <span className="ap-tag">
                  قیمت: {Number(priceRange.lo).toLocaleString('fa-IR')} تا {Number(priceRange.hi).toLocaleString('fa-IR')}
                  <button type="button" onClick={() => setPriceRange({ lo: priceBounds.min, hi: priceBounds.max })} aria-label="حذف فیلتر قیمت"><XIcon size={12} /></button>
                </span>
              )}
            </div>
          )}

          <Modal open={mobileFilter} onClose={() => setMobileFilter(false)} title="فیلترها" contentHeight="640px">
            {filterPanel}
          </Modal>

          <div className="ap-toolbar">
            <span className="ap-count">{total.toLocaleString('fa-IR')} محصول</span>
            <SortSelect value={sort} onChange={setSort} />
          </div>

          {loading ? (
            <div className="ap-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="ap-card">
                  <div className="ap-card-media"><Skeleton height="100%" /></div>
                  <div className="ap-card-body">
                    <Skeleton height="18px" width="90%" style={{ minHeight: 'calc(0.95rem * 1.35 * 2)' }} />
                    <div className="ap-card-footer">
                      <Skeleton height="14px" width="50%" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="ap-empty">
              <p>محصولی با این فیلترها پیدا نشد.</p>
              {hasActiveFilters && <button type="button" className="ap-clear-btn" onClick={clearFilters}>پاک کردن فیلترها</button>}
            </div>
          ) : (
            <div className="ap-grid">
              {products.map(p => (
                <Link to={`/products/${p.id}`} key={p.id} className="ap-card">
                  <div className="ap-card-media">
                    <img src={imgOf(p)} alt={p.name} loading="lazy" />
                  </div>
                  <div className="ap-card-body">
                    <div className="serif ap-card-name">{p.name}</div>
                    <div className="ap-card-footer">
                      <div className="ap-card-price">
                        از {Number(p.customization_data?.base_price || 0).toLocaleString('fa-IR')} تومان
                      </div>
                      <div className="ap-card-tags">
                        {p.attributes && Object.keys(p.attributes).length > 0 &&
                          Object.entries(p.attributes).filter(([k]) => k !== 'price').slice(0, 3).map(([k, v]) => (
                            <span key={k}>{v}</span>
                          ))}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {!loading && products.length < total && (
            <div className="ap-loadmore">
              <button type="button" className="ap-loadmore-btn" disabled={loadingMore} onClick={() => setPage(p => p + 1)}>
                {loadingMore ? 'در حال بارگذاری...' : 'نمایش محصولات بیشتر'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const apStyles = `
.ap-page {
  --ap-radius: 14px;
  padding-left: 20px;
  padding-right: 20px;
}
@media (min-width: 640px) {
  .ap-page { padding-left: 32px; padding-right: 32px; }
}
@media (min-width: 1024px) {
  .ap-page { padding-left: 56px; padding-right: 56px; }
}
@media (min-width: 1280px) {
  .ap-page { padding-left: 72px; padding-right: 72px; }
}

.ap-header { margin-bottom: 32px; }
.ap-title { font-weight: 600; font-size: clamp(1.5rem, 3.5vw, 2.1rem); margin: 0 0 6px; }
.ap-subtitle { color: var(--muted-fg); margin: 0; font-size: 0.95rem; }

.ap-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  align-items: start;
}
@media (min-width: 900px) {
  .ap-layout { grid-template-columns: 260px 1fr; }
}

/* -- sidebar (desktop only) -- */
.ap-sidebar { display: none; }
@media (min-width: 900px) {
  .ap-sidebar {
    display: block;
    position: sticky;
    top: 24px;
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: var(--ap-radius);
    padding: 24px;
  }
}

.ap-filters-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 18px;
}
.ap-clear-btn {
  background: none;
  border: none;
  color: var(--accent);
  font-size: 0.82rem;
  cursor: pointer;
  padding: 4px 0;
}
.ap-clear-btn:hover { text-decoration: underline; }

.ap-filter-group { margin-bottom: 20px; }
.ap-filter-group:last-child { margin-bottom: 0; }
.ap-filter-group > label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 10px;
  color: var(--fg);
}

.ap-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.ap-chip {
  padding: 6px 13px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: transparent;
  cursor: pointer;
  font-size: 0.82rem;
  color: var(--fg);
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.ap-chip:hover { border-color: var(--accent); }
.ap-chip.is-active {
  border-color: var(--accent);
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 10%, transparent);
}

/* -- search + toolbar -- */
.ap-searchbar { display: flex; gap: 8px; margin-bottom: 14px; }
.ap-search-input {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--border);
  border-radius: var(--ap-radius);
  padding: 0 14px;
  background: var(--card);
  color: var(--muted-fg);
}
.ap-search-input input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 12px 0;
  font-size: 0.92rem;
  color: var(--fg);
  outline: none;
}
.ap-search-clear {
  border: none;
  background: none;
  color: var(--muted-fg);
  cursor: pointer;
  display: flex;
  padding: 4px;
}
.ap-filter-toggle {
  position: relative;
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  border-radius: var(--ap-radius);
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--fg);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
@media (min-width: 900px) { .ap-filter-toggle { display: none; } }
.ap-filter-dot {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--accent);
}

.ap-active-filters { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
.ap-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  background: var(--muted);
  color: var(--muted-fg);
  padding: 5px 6px 5px 12px;
  border-radius: 999px;
}
.ap-tag button {
  border: none;
  background: rgba(0,0,0,0.06);
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: inherit;
}

.ap-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.ap-count { color: var(--muted-fg); font-size: 0.88rem; }

.ap-select { position: relative; }
.ap-select-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 9px 14px;
  font-size: 0.85rem;
  background: var(--card);
  color: var(--fg);
  cursor: pointer;
  transition: border-color 0.15s ease;
}
.ap-select-trigger:hover { border-color: var(--accent); }
.ap-select-chevron { transition: transform 0.18s ease; color: var(--muted-fg); }
.ap-select-chevron.is-open { transform: rotate(180deg); }

.ap-select-list {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 180px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.12);
  padding: 6px;
  list-style: none;
  margin: 0;
  z-index: 20;
  animation: ap-pop 0.15s ease;
}
@keyframes ap-pop {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
.ap-select-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: right;
  border: none;
  background: none;
  padding: 9px 10px;
  border-radius: 8px;
  font-size: 0.85rem;
  color: var(--fg);
  cursor: pointer;
}
.ap-select-option:hover { background: var(--muted); }
.ap-select-option.is-selected { color: var(--accent); font-weight: 600; }
.ap-select-option svg { flex-shrink: 0; }
.ap-select-option:not(.is-selected) svg { visibility: hidden; }

/* -- grid + cards -- */
.ap-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}
@media (min-width: 640px) { .ap-grid { grid-template-columns: repeat(3, 1fr); gap: 18px; } }
@media (min-width: 1100px) { .ap-grid { grid-template-columns: repeat(4, 1fr); } }

.ap-card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: var(--ap-radius);
  overflow: hidden;
  background: var(--card);
  text-decoration: none;
  color: inherit;
  transition: border-color 0.2s ease, transform 0.2s ease;
}
.ap-card:hover { border-color: var(--accent); transform: translateY(-2px); }

.ap-card-media { aspect-ratio: 1 / 1; overflow: hidden; background: var(--muted); }
.ap-card-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s ease;
}
.ap-card:hover .ap-card-media img { transform: scale(1.04); }

.ap-card-body { padding: 14px; display: flex; flex-direction: column; flex: 1; }
.ap-card-name {
  font-weight: 600;
  font-size: 0.95rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.35;
  min-height: calc(0.95rem * 1.35 * 2);
  flex-shrink: 0;
}
.ap-card-footer { margin-top: auto; padding-top: 10px; }
.ap-card-price { color: var(--accent); font-weight: 600; font-size: 0.88rem; }
.ap-card-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 8px; min-height: 22px; }
.ap-card-tags span {
  font-size: 0.72rem;
  background: var(--muted);
  color: var(--muted-fg);
  padding: 3px 8px;
  border-radius: 999px;
}

/* -- empty / load more -- */
.ap-empty {
  text-align: center;
  padding: 60px 20px;
  color: var(--muted-fg);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}
.ap-loadmore { text-align: center; margin-top: 28px; }
.ap-loadmore-btn {
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--fg);
  padding: 11px 26px;
  border-radius: 999px;
  font-size: 0.88rem;
  cursor: pointer;
  transition: border-color 0.15s ease;
}
.ap-loadmore-btn:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); }
.ap-loadmore-btn:disabled { opacity: 0.6; cursor: default; }

@media (prefers-reduced-motion: reduce) {
  .ap-card, .ap-card-media img { transition: none; }
}
`;
